/**
 * Sample Google Docs documents for agenda preview, marker parsing and revision-conflict tests.
 * Indexes follow the Docs API: the body starts with a section break at [0,1) and every paragraph
 * ends with a newline that is counted in its endIndex (UTF-16 code units).
 */
import { AGENDA_MARKERS, type DocsDocument, type DocsNamedRange, type DocsStructuralElement } from '../providers/google.js';

export interface DocParagraphSpec {
  text: string;
  style?: 'NORMAL_TEXT' | 'TITLE' | 'SUBTITLE' | 'HEADING_1' | 'HEADING_2' | 'HEADING_3';
  bullet?: boolean;
  link?: string;
}

export interface BuildDocsDocumentInput {
  documentId: string;
  title: string;
  revisionId: string;
  paragraphs: readonly DocParagraphSpec[];
  /** Named ranges keyed by name; each maps to inclusive paragraph index bounds. */
  namedRanges?: Readonly<Record<string, { fromParagraph: number; toParagraph: number }>>;
}

/** Build a Docs API document with correct UTF-16 offsets from a list of paragraphs. */
export function buildDocsDocument(input: BuildDocsDocumentInput): DocsDocument {
  const content: DocsStructuralElement[] = [{ startIndex: 0, endIndex: 1, sectionBreak: {} }];
  const paragraphRanges: { start: number; end: number }[] = [];
  let index = 1;
  input.paragraphs.forEach((p, i) => {
    const text = `${p.text}\n`;
    const start = index;
    const end = start + text.length;
    paragraphRanges.push({ start, end });
    const element: DocsStructuralElement = {
      startIndex: start,
      endIndex: end,
      paragraph: {
        elements: [
          {
            startIndex: start,
            endIndex: end,
            textRun: { content: text, textStyle: p.link ? { link: { url: p.link }, underline: true } : {} },
          },
        ],
        paragraphStyle: { namedStyleType: p.style ?? 'NORMAL_TEXT', ...(p.style?.startsWith('HEADING') ? { headingId: `h.${i}` } : {}) },
        ...(p.bullet ? { bullet: { listId: 'kix.list0', nestingLevel: 0 } } : {}),
      },
    };
    content.push(element);
    index = end;
  });
  const namedRanges: Record<string, { namedRanges: DocsNamedRange[] }> = {};
  for (const [name, bounds] of Object.entries(input.namedRanges ?? {})) {
    const from = paragraphRanges[bounds.fromParagraph];
    const to = paragraphRanges[bounds.toParagraph];
    if (!from || !to) throw new RangeError(`named range ${name} references a missing paragraph`);
    namedRanges[name] = { namedRanges: [{ namedRangeId: `nr.${name}`, name, ranges: [{ startIndex: from.start, endIndex: to.end }] }] };
  }
  return {
    documentId: input.documentId,
    title: input.title,
    revisionId: input.revisionId,
    body: { content },
    namedRanges,
    lists: { 'kix.list0': { listProperties: { nestingLevels: [{ glyphType: 'GLYPH_TYPE_UNSPECIFIED', glyphSymbol: '●' }] } } },
  };
}

/** Plain text of a document body (concatenated text runs). */
export function docPlainText(doc: DocsDocument): string {
  let out = '';
  for (const el of doc.body.content) {
    for (const pe of el.paragraph?.elements ?? []) out += pe.textRun?.content ?? '';
  }
  return out;
}

/** Paragraph indexes (into body.content) whose text equals `marker`. */
export function findMarkerParagraphs(doc: DocsDocument, marker: string): number[] {
  const out: number[] = [];
  doc.body.content.forEach((el, i) => {
    const text = (el.paragraph?.elements ?? []).map((pe) => pe.textRun?.content ?? '').join('').replace(/\n$/, '');
    if (text.trim() === marker) out.push(i);
  });
  return out;
}

export const SAMPLE_AGENDA_DOCUMENT_ID = '1AgencySampleAgendaDocHalcyonRobotics0000000001';
export const SAMPLE_AGENDA_REVISION_ID = 'ALBJ4LtSampleRevision0001';
export const SAMPLE_AGENDA_STALE_REVISION_ID = 'ALBJ4LtSampleRevision0002';

/**
 * Operator-authored agenda with legacy marker pairs. Text outside the markers is human-owned and
 * must survive an update; the block between VANTA_ASKS markers was written by an earlier tool run.
 */
export const SAMPLE_AGENDA_PARAGRAPHS: readonly DocParagraphSpec[] = [
  { text: 'Halcyon Robotics — Security programme agenda', style: 'TITLE' },
  { text: 'Meeting date: 2026-09-30', style: 'SUBTITLE' },
  { text: 'Advisories', style: 'HEADING_1' },
  { text: 'Vendor advisory for the build pipeline runner image is under review; no action needed before the next call.' },
  { text: 'Agenda items', style: 'HEADING_1' },
  { text: 'Confirm owner for the screen-lock remediation across the field team laptops.', bullet: true },
  { text: 'Decide whether contractors are in scope for security awareness training this quarter.', bullet: true },
  { text: 'Current blockers', style: 'HEADING_1' },
  { text: 'MDM enrollment for the two lab machines is blocked on a procurement ticket.' },
  { text: 'Customer asks', style: 'HEADING_1' },
  { text: AGENDA_MARKERS.asks.start },
  { text: 'Overall', style: 'HEADING_2' },
  { text: 'Screen lock — Enable automatic screen lock (15 minutes or less) on the following computers: aisha-mbp-1f2c, mateo-mba-9a10, yuki-mbp-0c44', bullet: true },
  { text: 'Employee Compliance', style: 'HEADING_2' },
  { text: 'Policy acceptance — Ask the following people to accept the outstanding policies in Vanta (4 people): Aisha Okonkwo, Mateo Lindqvist, Yuki Moreau, Oluwaseun Tanaka', bullet: true },
  { text: AGENDA_MARKERS.asks.end },
  { text: 'Framework status', style: 'HEADING_1' },
  { text: AGENDA_MARKERS.frameworks.start },
  { text: 'SOC 2: controls 35/64 · tests 278/364 · documents 43/59', bullet: true },
  { text: 'ISO 27001: controls 44/63 · tests 250/331 · documents 49/64', bullet: true },
  { text: AGENDA_MARKERS.frameworks.end },
  { text: 'Relevant links', style: 'HEADING_1' },
  { text: 'Infrastructure report (Google Sheet)', bullet: true, link: 'https://docs.google.com/spreadsheets/d/1AgencySampleInfraSheet0000000000000000001' },
  { text: 'Vanta dashboard', bullet: true, link: 'https://app.vanta.com/halcyon-robotics' },
  { text: 'Previously completed work', style: 'HEADING_1' },
  { text: 'Enabled GuardDuty in us-west-2 and eu-west-1.', bullet: true },
  { text: 'Rotated the two IAM access keys flagged in the previous sync.', bullet: true },
];

export const SAMPLE_AGENDA_DOCUMENT: DocsDocument = buildDocsDocument({
  documentId: SAMPLE_AGENDA_DOCUMENT_ID,
  title: 'Halcyon Robotics — Security programme agenda',
  revisionId: SAMPLE_AGENDA_REVISION_ID,
  paragraphs: SAMPLE_AGENDA_PARAGRAPHS,
});

/** Same content after an external edit: bumped revision, extra operator paragraph inside a human section. */
export const SAMPLE_AGENDA_DOCUMENT_EDITED_EXTERNALLY: DocsDocument = buildDocsDocument({
  documentId: SAMPLE_AGENDA_DOCUMENT_ID,
  title: 'Halcyon Robotics — Security programme agenda',
  revisionId: SAMPLE_AGENDA_STALE_REVISION_ID,
  paragraphs: [
    ...SAMPLE_AGENDA_PARAGRAPHS.slice(0, 9),
    { text: 'Procurement approved the lab machine replacements on 2026-09-24.' },
    ...SAMPLE_AGENDA_PARAGRAPHS.slice(9),
  ],
});

/** Damaged document: the asks end marker was deleted by hand (update must stop and offer repair). */
export const SAMPLE_AGENDA_DOCUMENT_MISSING_END_MARKER: DocsDocument = buildDocsDocument({
  documentId: '1AgencySampleAgendaDocDamaged00000000000000001',
  title: 'Halcyon Robotics — Security programme agenda (damaged)',
  revisionId: 'ALBJ4LtDamagedRevision0001',
  paragraphs: SAMPLE_AGENDA_PARAGRAPHS.filter((p) => p.text !== AGENDA_MARKERS.asks.end),
});

/** Damaged document: duplicated start marker. */
export const SAMPLE_AGENDA_DOCUMENT_DUPLICATE_MARKERS: DocsDocument = buildDocsDocument({
  documentId: '1AgencySampleAgendaDocDuplicate0000000000000001',
  title: 'Halcyon Robotics — Security programme agenda (duplicate markers)',
  revisionId: 'ALBJ4LtDuplicateRevision0001',
  paragraphs: SAMPLE_AGENDA_PARAGRAPHS.flatMap((p) => (p.text === AGENDA_MARKERS.asks.start ? [p, p] : [p])),
});

/** Agency-created document using named ranges instead of legacy markers. */
export const SAMPLE_AGENDA_DOCUMENT_NAMED_RANGES: DocsDocument = (() => {
  const markerTexts: readonly string[] = [AGENDA_MARKERS.asks.start, AGENDA_MARKERS.asks.end, AGENDA_MARKERS.frameworks.start, AGENDA_MARKERS.frameworks.end];
  const paragraphs = SAMPLE_AGENDA_PARAGRAPHS.filter((p) => !markerTexts.includes(p.text));
  const asksFrom = paragraphs.findIndex((p) => p.text === 'Overall');
  const asksTo = paragraphs.findIndex((p) => p.text.startsWith('Policy acceptance'));
  const fwFrom = paragraphs.findIndex((p) => p.text.startsWith('SOC 2:'));
  const fwTo = paragraphs.findIndex((p) => p.text.startsWith('ISO 27001:'));
  return buildDocsDocument({
    documentId: '1AgencySampleAgendaDocNamedRanges000000000000001',
    title: 'Halcyon Robotics — Security programme agenda',
    revisionId: 'ALBJ4LtNamedRangeRevision0001',
    paragraphs,
    namedRanges: {
      [AGENDA_MARKERS.asks.namedRange]: { fromParagraph: asksFrom, toParagraph: asksTo },
      [AGENDA_MARKERS.frameworks.namedRange]: { fromParagraph: fwFrom, toParagraph: fwTo },
    },
  });
})();

/** Empty document as returned by documents.create (title only, one empty paragraph). */
export const SAMPLE_EMPTY_DOCUMENT: DocsDocument = buildDocsDocument({
  documentId: '1AgencySampleEmptyDoc0000000000000000000000001',
  title: 'Untitled agenda',
  revisionId: 'ALBJ4LtEmptyRevision0001',
  paragraphs: [{ text: '' }],
});

export const SAMPLE_GOOGLE_DOCUMENTS: readonly DocsDocument[] = [
  SAMPLE_AGENDA_DOCUMENT,
  SAMPLE_AGENDA_DOCUMENT_EDITED_EXTERNALLY,
  SAMPLE_AGENDA_DOCUMENT_MISSING_END_MARKER,
  SAMPLE_AGENDA_DOCUMENT_DUPLICATE_MARKERS,
  SAMPLE_AGENDA_DOCUMENT_NAMED_RANGES,
  SAMPLE_EMPTY_DOCUMENT,
];
